import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, LogOut, PanelRightClose } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * TopNavigationDesktop Component
 * 
 * Desktop version of the top navigation bar.
 * Exact copy of original TopNavigation with touch feedback added.
 */
const TopNavigationDesktop = ({
  activeView = 'chat',
  onViewChange,
  theme = 'dark',
  onThemeToggle,
  onProfileClick,
  onRightSidebarToggle
}) => {
  const navigate = useNavigate();
  const { currentUser, userData, signOut } = useAuth();
  const [themeToggleHovered, setThemeToggleHovered] = useState(false);
  const [sidebarToggleHovered, setSidebarToggleHovered] = useState(false);
  const [profileHovered, setProfileHovered] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBrandClick = () => {
    navigate('/app');
  };

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      navigate('/profile');
    }
  };

  const handleTabClick = (view) => {
    if (onViewChange) {
      onViewChange(view);
    }
  };

  const tabs = [
    { id: 'taxper', label: 'Taxper' },
    { id: 'chat', label: 'Chat' },
    { id: 'contly', label: 'Contly' },
  ];

  return (
    <>
      <header
        className="sticky top-0 w-full h-[56px] md:h-[60px] z-[100] overflow-hidden transition-all duration-300"
        style={{
          background: theme === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(248, 249, 250, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: theme === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(148, 163, 184, 0.6)',
          boxShadow: theme === 'dark'
            ? '0 18px 40px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px 0 rgba(15, 23, 42, 0.1), 0 2px 8px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset 0 -1px 0 rgba(148, 163, 184, 0.2)',
        }}
        data-theme={theme}
        onMouseEnter={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
          } else {
            e.currentTarget.style.background = 'rgba(249, 250, 251, 0.8)';
            e.currentTarget.style.boxShadow = '0 12px 48px 0 rgba(15, 23, 42, 0.15), 0 4px 16px 0 rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9), inset 0 -1px 0 rgba(148, 163, 184, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (theme === 'dark') {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
          } else {
            e.currentTarget.style.background = 'rgba(248, 249, 250, 0.7)';
            e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(15, 23, 42, 0.1), 0 2px 8px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset 0 -1px 0 rgba(148, 163, 184, 0.2)';
          }
        }}
      >
        {/* Animated Shimmer Overlay */}
        <div
          className="absolute top-0 left-[-100%] w-[200%] h-full pointer-events-none"
          style={{
            background: theme === 'dark'
              ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.03) 50%, transparent 100%)'
              : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
            animation: 'shimmerNav 8s ease-in-out infinite',
          }}
        />

        {/* Inner Container */}
        <div className="max-w-[1200px] mx-auto px-4 h-full flex items-center justify-between gap-3 md:gap-3 relative z-[2]">
          {/* Brand Section - Left */}
          <div className="flex flex-col items-start gap-[2px] flex-shrink-0">
            <div
              className="text-[18px] font-bold leading-none transition-opacity duration-200 ease-linear cursor-pointer md:text-[18px]"
              style={{
                color: theme === 'dark' ? '#ffffff' : '#0f172a',
                letterSpacing: '0.030em',
              }}
              onClick={handleBrandClick}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <span className="lawnance-brand">LAWNANCE</span>
            </div>
            <div
              className="text-[10px] font-medium uppercase leading-none md:text-[10px]"
              style={{
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#475569',
                letterSpacing: '0.02em',
                fontWeight: theme === 'light' ? '600' : '500',
              }}
            >
              Legal & Finance Copilot
            </div>
          </div>

          {/* Navigation Tabs - Center */}
          <div className="flex-1 flex justify-center items-center">
            <div
              className="inline-flex items-center gap-1 md:gap-1.5 p-[5px] md:p-1.5 rounded-full relative overflow-visible"
              style={{
                background: theme === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(248, 249, 250, 0.6)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: theme === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.15)'
                  : '1px solid rgba(148, 163, 184, 0.6)',
                boxShadow: theme === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.35)'
                  : '0 4px 16px 0 rgba(15, 23, 42, 0.08), 0 1px 4px 0 rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.7), inset 0 -1px 0 rgba(148, 163, 184, 0.2)',
              }}
            >
              {/* Inner border overlay */}
              <div
                className="absolute top-0 left-0 right-0 bottom-0 rounded-full pointer-events-none z-[1]"
                style={{
                  border: theme === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.8)',
                }}
              />

              {tabs.map((tab) => {
                const isActive = activeView === tab.id;
                return (
                  <button
                    key={tab.id}
                    data-view={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className="relative z-[2] whitespace-nowrap transition-all duration-300 text-[13px] md:text-[14px] px-4 md:px-6 py-[7px] md:py-2 active:scale-95"
                    style={{
                      background: isActive
                        ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(249, 250, 251, 0.85)')
                        : 'transparent',
                      border: isActive
                        ? '1.5px solid rgba(59, 130, 246, 0.8)'
                        : 'none',
                      borderRadius: '999px',
                      fontWeight: isActive ? '600' : '500',
                      cursor: 'pointer',
                      color: isActive
                        ? (theme === 'dark' ? 'rgba(255, 255, 255, 1)' : '#0f172a')
                        : (theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#0f172a'),
                      letterSpacing: '-0.01em',
                      fontFamily: 'inherit',
                      boxShadow: isActive
                        ? theme === 'dark'
                          ? '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.4), 0 0 12px rgba(59, 130, 246, 0.3)'
                          : '0 4px 12px rgba(15, 23, 42, 0.12), 0 2px 4px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.95), inset 0 -1px 0 rgba(148, 163, 184, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.4), 0 0 12px rgba(59, 130, 246, 0.25)'
                        : 'none',
                      backdropFilter: isActive ? 'blur(10px)' : 'none',
                      WebkitBackdropFilter: isActive ? 'blur(10px)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#0f172a';
                        e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.boxShadow = theme === 'dark'
                          ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                          : 'inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#0f172a';
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions Section - Right */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Right Sidebar Toggle Button - Only show for Taxper and Contly */}
           

            {/* Theme Toggle Button */}
            <button
              className="nav-action-btn theme-toggle relative rounded-full cursor-pointer transition-all duration-300 overflow-hidden active:scale-[0.98]"
              title="Toggle theme"
              aria-label="Toggle theme"
              onClick={onThemeToggle}
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: themeToggleHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(100, 116, 139, 0.7)')
                  : (theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(148, 163, 184, 0.6)'),
                background: themeToggleHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(51, 65, 85, 0.12)')
                  : (theme === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%), rgba(51, 65, 85, 0.08)'),
                backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                color: themeToggleHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 1)' : '#0f172a')
                  : (theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#0f172a'),
                boxShadow: themeToggleHovered
                  ? (theme === 'dark'
                    ? '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 4px 16px rgba(15, 23, 42, 0.15), 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset 0 -1px 0 rgba(148, 163, 184, 0.3)')
                  : (theme === 'dark'
                    ? 'none'
                    : '0 2px 8px 0 rgba(15, 23, 42, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7), inset 0 -1px 0 rgba(255, 255, 255, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.15), inset 0 -2px 0 rgba(148, 163, 184, 0.15)'),
                transform: themeToggleHovered ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={() => setThemeToggleHovered(true)}
              onMouseLeave={() => setThemeToggleHovered(false)}
            >
              {/* Hover ripple effect */}
              <div
                className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
                style={{
                  width: themeToggleHovered ? '100%' : '0',
                  height: themeToggleHovered ? '100%' : '0',
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  transform: 'translate(-50%, -50%)',
                  transition: 'width 0.3s ease, height 0.3s ease',
                }}
              />

              {/* Theme Icons */}
              {theme === 'dark' ? (
                <Moon
                  size={18}
                  className="relative z-[1]"
                  style={{
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
              ) : (
                <Sun
                  size={18}
                  className="relative z-[1]"
                  style={{
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
              )}
            </button>

            {/* Welcome Message */}
            {currentUser && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(51, 65, 85, 0.08)',
                  border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(148, 163, 184, 0.6)',
                }}
              >
                <span
                  className="text-sm font-medium whitespace-nowrap"
                  style={{
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#0f172a',
                  }}
                >
                  Welcome, {currentUser.displayName || 'User'}
                </span>
              </div>
            )}

            {/* Logout Button */}
            <button
              className="nav-action-btn relative rounded-full cursor-pointer transition-all duration-300 overflow-hidden active:scale-[0.98]"
              title="Logout"
              aria-label="Logout"
              onClick={handleLogout}
              style={{
                width: '36px',
                height: '36px',
                border: logoutHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(100, 116, 139, 0.7)')
                  : (theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(148, 163, 184, 0.6)'),
                background: logoutHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(51, 65, 85, 0.12)')
                  : (theme === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%), rgba(51, 65, 85, 0.08)'),
                backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                color: logoutHovered
                  ? (theme === 'dark' ? 'rgba(255, 255, 255, 1)' : '#0f172a')
                  : (theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#0f172a'),
                boxShadow: logoutHovered
                  ? (theme === 'dark'
                    ? '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 4px 16px rgba(15, 23, 42, 0.15), 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset 0 -1px 0 rgba(148, 163, 184, 0.3)')
                  : (theme === 'dark'
                    ? 'none'
                    : '0 2px 8px 0 rgba(15, 23, 42, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7), inset 0 -1px 0 rgba(255, 255, 255, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.15), inset 0 -2px 0 rgba(148, 163, 184, 0.15)'),
                transform: logoutHovered ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={() => setLogoutHovered(true)}
              onMouseLeave={() => setLogoutHovered(false)}
            >
              {/* Hover ripple effect */}
              <div
                className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
                style={{
                  width: logoutHovered ? '100%' : '0',
                  height: logoutHovered ? '100%' : '0',
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  transform: 'translate(-50%, -50%)',
                  transition: 'width 0.3s ease, height 0.3s ease',
                }}
              />

              <LogOut
                size={18}
                className="relative z-[1]"
                style={{
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </button>
             {(activeView === 'taxper' || activeView === 'contly') && onRightSidebarToggle && (
              <button
                className="nav-action-btn sidebar-toggle relative rounded-full cursor-pointer transition-all duration-300 overflow-hidden active:scale-[0.98]"
                title="Toggle sidebar"
                aria-label="Toggle sidebar"
                onClick={onRightSidebarToggle}
                style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: sidebarToggleHovered
                    ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(100, 116, 139, 0.7)')
                    : (theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(148, 163, 184, 0.6)'),
                  background: sidebarToggleHovered
                    ? (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(51, 65, 85, 0.12)')
                    : (theme === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%), rgba(51, 65, 85, 0.08)'),
                  backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px) saturate(180%)',
                  color: sidebarToggleHovered
                    ? (theme === 'dark' ? 'rgba(255, 255, 255, 1)' : '#0f172a')
                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#0f172a'),
                  boxShadow: sidebarToggleHovered
                    ? (theme === 'dark'
                      ? '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                      : '0 4px 16px rgba(15, 23, 42, 0.15), 0 2px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8), inset 0 -1px 0 rgba(148, 163, 184, 0.3)')
                    : (theme === 'dark'
                      ? 'none'
                      : '0 2px 8px 0 rgba(15, 23, 42, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7), inset 0 -1px 0 rgba(255, 255, 255, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.15), inset 0 -2px 0 rgba(148, 163, 184, 0.15)'),
                  transform: sidebarToggleHovered ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setSidebarToggleHovered(true)}
                onMouseLeave={() => setSidebarToggleHovered(false)}
              >
                <div
                  className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
                  style={{
                    width: sidebarToggleHovered ? '100%' : '0',
                    height: sidebarToggleHovered ? '100%' : '0',
                    background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                    transform: 'translate(-50%, -50%)',
                    transition: 'width 0.3s ease, height 0.3s ease',
                  }}
                />
                <PanelRightClose
                  size={18}
                  className="relative z-[1]"
                  style={{
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default TopNavigationDesktop;

